import useRequest from "@/lib/request/useRequest";
import { Organization } from "@/types/organization";
import {
    faArrowDownShortWide,
    faArrowUpShortWide,
    faFill,
    faMap,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FeatureCollectionWithFilename } from "shpjs";
import { Feature } from "geojson";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Key } from "@react-types/shared";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Checkbox,
    Input,
} from "@heroui/react";
import { array, object, number, string } from "yup";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import {
    BULK_FIELD_FILL_LAYER,
    BULK_FIELD_SOURCE,
    BULK_FIELD_TEXT_LAYER,
} from "@/lib/map/layers";
import { LngLatBoundsLike } from "mapbox-gl";
import * as turf from "@turf/turf";
import useAuth from "@/lib/auth/useAuth";

const schema = array()
    .of(
        object().shape({
            properties: object().shape({
                grower: number().required().label("Grower"),
                field_name: string().required().label("Field Name"),
            }),
        }),
    )
    .min(1, "At least one field is required");

function AddBulkFieldTable({
    geojson,
    selectedProperty,
    onSavePressed,
    isLoading,
}: {
    geojson?: FeatureCollectionWithFilename;
    selectedProperty: Key | undefined | null;
    onSavePressed: (feautres: Feature[]) => void;
    isLoading: boolean;
}) {
    const { useGetRequest } = useRequest();
    const organizationsQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
        method: "get",
    });
    const { isConsultantOrSentinel } = useAuth();

    const organizationOptions = useMemo(() => {
        return (
            organizationsQuery.data?.map((organization) => ({
                label: organization.name,
                value: organization.id,
            })) ?? []
        );
    }, [organizationsQuery.data]);

    const [features, setFeatures] = useState<Feature[]>([]);
    const { map, mapLoaded } = useMap();

    const columns = useMemo(() => {
        const cols = [
            {
                header: "Field Name",
                enableSorting: false,
                accessorFn: (row) =>
                    selectedProperty
                        ? row.properties?.[selectedProperty]
                        : null,
                size: 250,
                cell: ({ row, getValue, table }) => {
                    const initialValue = getValue() as string;
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const [value, setValue] = useState(initialValue);

                    const onBlur = () => {
                        // @ts-expect-error - table meta
                        table.options.meta?.updateData(
                            row.index,
                            selectedProperty,
                            value,
                        );
                    };

                    // If the initialValue is changed external, sync it up with our state
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    if (!selectedProperty) return null;

                    return (
                        <Input
                            size="sm"
                            value={value}
                            isInvalid={
                                row.getIsSelected() &&
                                !row.original?.properties?.[selectedProperty]
                            }
                            onChange={(e) => {
                                setValue(e.target.value);
                            }}
                            onBlur={onBlur}
                        />
                    );
                },
            },
        ] as ColumnDef<Feature>[];

        if (isConsultantOrSentinel) {
            cols.splice(0, 0, {
                id: "selection",
                enableSorting: false,
                header: ({ table }) => {
                    if (!selectedProperty) {
                        return (
                            <Checkbox
                                isDisabled
                                isReadOnly
                                aria-label="Select all"
                            />
                        );
                    }
                    return (
                        <Checkbox
                            aria-label="Select all"
                            isSelected={table.getIsAllRowsSelected()}
                            onValueChange={(value) => {
                                table.toggleAllRowsSelected(value);
                            }}
                        />
                    );
                },
                size: 50,
                accessorFn: (row) => row?.id ?? "",
                cell: ({ row }) => {
                    if (!selectedProperty) {
                        return (
                            <Checkbox
                                isDisabled
                                isReadOnly
                                aria-label="Select"
                            />
                        );
                    }
                    return (
                        <Checkbox
                            aria-label="Select"
                            isSelected={row.getIsSelected()}
                            onValueChange={(value) => row.toggleSelected(value)}
                        ></Checkbox>
                    );
                },
            });
            cols.push({
                header: "Grower",
                accessorKey: "grower",
                size: 200,
                enableSorting: false,
                cell: ({ row }) => {
                    if (!selectedProperty) return null;
                    return (
                        <div className="w-full">
                            <Autocomplete
                                size="sm"
                                aria-label="Grower"
                                selectedKey={
                                    row.original.properties?.grower ?? null
                                }
                                isInvalid={
                                    row.getIsSelected() &&
                                    !row.original.properties?.grower
                                }
                                onSelectionChange={(e) => {
                                    setFeatures((features) =>
                                        features.map((feature) => {
                                            if (!feature.properties) {
                                                feature.properties = {};
                                            }
                                            if (
                                                row.original.properties?.[
                                                    selectedProperty
                                                ] !==
                                                feature.properties[
                                                    selectedProperty
                                                ]
                                            )
                                                return feature;
                                            feature.properties.grower = e;
                                            return feature;
                                        }),
                                    );
                                }}
                            >
                                {organizationOptions.map((organization) => (
                                    <AutocompleteItem key={organization.value}>
                                        {organization.label}
                                    </AutocompleteItem>
                                ))}
                            </Autocomplete>
                        </div>
                    );
                },
            });
        }
        cols.push({
            header: "Action",
            accessorKey: "action",
            size: 50,
            enableSorting: false,
            cell: ({ row }) => {
                return (
                    <Button
                        size="sm"
                        variant="light"
                        color="primary"
                        isIconOnly
                        onPress={() => {
                            if (!map || !mapLoaded) return;
                            map.fitBounds(
                                turf.bbox(
                                    row.original.geometry,
                                ) as LngLatBoundsLike,
                                {
                                    padding: 40,
                                    duration: 0,
                                },
                            );
                        }}
                    >
                        <FontAwesomeIcon icon={faMap} />
                    </Button>
                );
            },
        });
        return cols;
    }, [
        isConsultantOrSentinel,
        map,
        mapLoaded,
        organizationOptions,
        selectedProperty,
    ]);

    console.log("render");

    useEffect(() => {
        if (selectedProperty) {
            setFeatures((x) => [...x]);
        }
    }, [selectedProperty]);

    useEffect(() => {
        if (geojson?.features && geojson?.features?.length > 0) {
            setFeatures(
                geojson?.features?.map((feature, index) => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        index: index,
                    },
                })),
            );
        }
    }, [geojson]);

    const table = useReactTable({
        data: features,
        columns,
        getCoreRowModel: getCoreRowModel(),
        meta: {
            updateData: (
                selectedIndex: number,
                property: string,
                value: string,
            ) => {
                setFeatures((features) =>
                    features.map((feature, index) => {
                        if (index === selectedIndex) {
                            return {
                                ...feature,
                                properties: {
                                    ...feature.properties,
                                    [property]: value,
                                },
                            };
                        }
                        return feature;
                    }),
                );
            },
        },
    });

    const ref = useRef<HTMLDivElement>(null);

    const { rows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => 33,
        getScrollElement: () => ref.current,
        measureElement:
            typeof window !== "undefined" &&
            navigator.userAgent.indexOf("Firefox") === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
        overscan: 5,
    });

    const [bulkFillOrganization, setBulkFillOrganization] = useState<
        Key | undefined | null
    >(undefined);

    const { rows: selectedRows } = table.getSelectedRowModel();

    const selectedFieldIds = useMemo(() => {
        if (!selectedProperty) return [];

        return selectedRows.map(
            (row) => row.original.properties?.[selectedProperty],
        );
    }, [selectedRows, selectedProperty]);

    useEffect(() => {
        if (selectedProperty) {
            table.toggleAllRowsSelected(true);
        } else {
            table.toggleAllRowsSelected(false);
        }
    }, [selectedProperty, table]);

    const handleBulkFill = useCallback(() => {
        if (!bulkFillOrganization || !selectedProperty) return;
        setFeatures((features) =>
            features.map((feature) => {
                if (
                    !feature.properties ||
                    !selectedFieldIds.includes(
                        feature.properties[selectedProperty],
                    )
                )
                    return feature;
                feature.properties["grower"] = bulkFillOrganization;
                return feature;
            }),
        );
    }, [bulkFillOrganization, selectedProperty, selectedFieldIds]);

    const isValid = useMemo(() => {
        if (isConsultantOrSentinel) {
            if (!selectedRows || !selectedProperty) return false;
            return schema.isValidSync(
                selectedRows.map((row) => ({
                    properties: {
                        grower: row.original.properties?.grower,
                        field_name: row.original.properties?.[selectedProperty],
                    },
                })),
            );
        }
        return !!selectedProperty;
    }, [isConsultantOrSentinel, selectedProperty, selectedRows]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (geojson && geojson.features.length > 0) {
            if (!map.getSource(BULK_FIELD_SOURCE)) {
                map.fitBounds(turf.bbox(geojson) as LngLatBoundsLike, {
                    padding: 20,
                    duration: 0,
                });
            }

            addUpdateGeojsonSource(map, BULK_FIELD_SOURCE, {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: geojson.features.map((feature) => ({
                        ...feature,
                        properties: {
                            ...feature.properties,
                            field_display_name: selectedProperty
                                ? feature.properties?.[selectedProperty]
                                : "",
                            selected:
                                selectedProperty &&
                                selectedFieldIds.includes(
                                    feature.properties?.[selectedProperty],
                                ),
                        },
                    })),
                },
                tolerance: 0,
            });

            addUpdateGeojsonLayers(map, [
                BULK_FIELD_FILL_LAYER,
                BULK_FIELD_TEXT_LAYER,
            ]);
        } else {
            removeLayersIfExists(map, [
                BULK_FIELD_FILL_LAYER.id,
                BULK_FIELD_TEXT_LAYER.id,
            ]);
            removeSourcesIfExists(map, [BULK_FIELD_SOURCE]);
        }

        return () => {
            removeLayersIfExists(map, [
                BULK_FIELD_FILL_LAYER.id,
                BULK_FIELD_TEXT_LAYER.id,
            ]);
            removeSourcesIfExists(map, [BULK_FIELD_SOURCE]);
        };
    }, [map, mapLoaded, geojson, selectedProperty, selectedFieldIds]);

    return (
        <div>
            <div className="font-semibold flex justify-between items-center pb-2">
                <div>Imported Fields</div>
                {isConsultantOrSentinel && (
                    <div className="flex flex-row gap-2 items-center">
                        <Autocomplete
                            size="sm"
                            aria-label="Grower Fill"
                            selectedKey={bulkFillOrganization}
                            onSelectionChange={(value) =>
                                setBulkFillOrganization(value)
                            }
                            labelPlacement="outside-left"
                            label="Bulk Fill Grower"
                        >
                            {organizationOptions.map((organization) => (
                                <AutocompleteItem key={organization.value}>
                                    {organization.label}
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                        <Button
                            onPress={handleBulkFill}
                            size="sm"
                            color="primary"
                            isIconOnly
                        >
                            <FontAwesomeIcon icon={faFill} />
                        </Button>
                    </div>
                )}
            </div>
            <div
                ref={ref}
                key={selectedProperty?.toString()}
                className="max-h-[400px] overflow-y-scroll"
            >
                <table style={{ display: "grid" }}>
                    <thead
                        style={{
                            display: "grid",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                        className="bg-gray-100 rounded-md"
                    >
                        {table.getHeaderGroups().map((group) => (
                            <tr key={group.id}>
                                {group.headers.map((header) => {
                                    return (
                                        <th
                                            aria-label={
                                                header.column.columnDef.header?.toString() ??
                                                "column header"
                                            }
                                            key={header.id}
                                            style={{
                                                width: header.column.getSize(),
                                            }}
                                            className="pointer-events-none"
                                        >
                                            <div
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    width: header.column.getSize(),
                                                }}
                                                className={classNames({
                                                    "rounded-md py-2 text-medium pointer-events-auto flex items-center":
                                                        true,
                                                    "cursor-pointer hover:bg-gray-200 ":
                                                        header.column.getCanSort(),
                                                })}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {header.column.getCanSort() &&
                                                    header.column.getIsSorted() && (
                                                        <FontAwesomeIcon
                                                            className="ml-2 text-primary-500"
                                                            icon={
                                                                header.column.getIsSorted() ===
                                                                "asc"
                                                                    ? faArrowUpShortWide
                                                                    : faArrowDownShortWide
                                                            }
                                                            size="sm"
                                                        />
                                                    )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        style={{
                            display: "grid",
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            position: "relative",
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[virtualRow.index] as Row<Feature>;
                            return (
                                <tr
                                    data-index={virtualRow.index}
                                    ref={(node) =>
                                        rowVirtualizer.measureElement(node)
                                    }
                                    key={row.id}
                                    style={{
                                        display: "flex",
                                        position: "absolute",
                                        transform: `translateY(${virtualRow.start}px)`,
                                        width: "100%",
                                    }}
                                    className="border-b py-[0.25rem] border-gray-300"
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                style={{
                                                    display: "flex",
                                                    width: cell.column.getSize(),
                                                }}
                                                className="items-center"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {!isValid && (
                <div className="text-red-500 text-sm py-2">
                    Please make sure every selected field has a name and grower.
                </div>
            )}
            <div className="mt-4 flex items-center justify-end">
                <Button
                    isDisabled={!isValid || isLoading}
                    isLoading={isLoading}
                    onPress={() => {
                        onSavePressed(selectedRows.map((row) => row.original));
                    }}
                    size="sm"
                    color="primary"
                >
                    Create Fields
                </Button>
            </div>
        </div>
    );
}

export default AddBulkFieldTable;
